import { SubscriptionBillingStatusEnum } from '@monetize/types/app';
import { createColumnHelper } from '../../../components/Monetize/DataTableNew/appTable';
import { actionCatalogColumn } from '../../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  dateBodyTemplate,
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getColumnFilterValuesForEnum,
  idBodyTemplate,
  nameWithIdBodyTemplate,
  textBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import { getBillGroupPageRoute } from '../../../constants/routes';
import { SubscriptionBillingStatusDisplayText } from '../../../constants/subscriptions';
import { ISubscriptionWithBillGroup } from '../../../hooks/AccountDetail/useSubscriptions';

const columnHelper = createColumnHelper<ISubscriptionWithBillGroup>();

const statusFilterValues = getColumnFilterValuesForEnum(
  SubscriptionBillingStatusEnum,
  SubscriptionBillingStatusDisplayText,
);

export type SubscriptionListColumnContext =
  ColumnContextBase<ISubscriptionWithBillGroup>;

/** tableId: external (hook-managed filters) */
export const SUBSCRIPTION_LIST_CATALOG: ColumnCatalog<
  ISubscriptionWithBillGroup,
  SubscriptionListColumnContext
> = [
  {
    id: 'id',
    label: 'ID',
    customizable: false,
    build: () =>
      columnHelper.accessor('id', {
        ...getColHeaderWithSize('ID', 150),
        cell: idBodyTemplate(),
        enableSorting: false,
      }),
  },
  {
    id: 'billGroupId',
    label: 'Bill Group',
    build: () =>
      columnHelper.accessor('billGroupId', {
        ...getColHeaderWithSize('Bill Group', 200),
        cell: nameWithIdBodyTemplate<ISubscriptionWithBillGroup, any>({
          nameProp: 'billGroup.name',
          idProp: 'billGroup.id',
          idLinkFn: (id) => getBillGroupPageRoute(id),
          fallback: '',
        }),
        enableSorting: false,
      }),
  },
  {
    id: 'offeringName',
    label: 'Name',
    build: () =>
      columnHelper.accessor('offeringName', {
        ...getColHeaderWithSize('Name', 200),
        cell: textBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'description',
    label: 'Description',
    build: () =>
      columnHelper.accessor('description', {
        ...getColHeaderWithSize('Description', 200),
        cell: textBodyTemplate(),
        enableSorting: false,
      }),
  },
  {
    id: 'periodStartDate',
    label: 'Period Start',
    build: () =>
      columnHelper.accessor('periodStartDate', {
        ...getColHeaderWithSize('Period Start', 150),
        cell: dateBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'periodEndDate',
    label: 'Period End',
    build: () =>
      columnHelper.accessor('periodEndDate', {
        ...getColHeaderWithSize('Period End', 150),
        cell: dateBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'billingStatus',
    label: 'Status',
    build: () =>
      columnHelper.accessor('billingStatus', {
        ...getColHeaderWithSize('Status', 150),
        cell: enumDisplayBodyTemplate<
          ISubscriptionWithBillGroup,
          SubscriptionBillingStatusEnum
        >({ displayMap: SubscriptionBillingStatusDisplayText }),
        enableSorting: true,
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: statusFilterValues,
        }),
      }),
  },
  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        ...getColHeaderWithSize('Updated', 170),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'createDate',
    label: 'Created',
    build: () =>
      columnHelper.accessor('createDate', {
        ...getColHeaderWithSize('Created', 170),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  actionCatalogColumn<ISubscriptionWithBillGroup>(),
];
